import {
  useCallback,
  useRef,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';
import { getAudioAnalyzer, getVolume } from 'recording/services/media-visualizer-helper';

const POST_SPEECH_SILENCE_MS = 1000;
const PRE_SPEECH_SILENCE_MS = 5000;
const SILENCE_THRESHOLD = 0.40;

// Inworld's streaming STT only accepts raw LINEAR16 PCM, 16kHz, mono — the AudioContext
// is created at this rate so the browser resamples the mic input for us.
const PCM_SAMPLE_RATE = 16000;
const PCM_PROCESSOR_NAME = 'pcm-capture-processor';
// ~160ms of audio per chunk sent over the socket — small enough to keep latency low,
// large enough to avoid flooding the connection with a message per render quantum (128 samples).
const PCM_CHUNK_SAMPLES = 2560;

// Runs inside AudioWorkletGlobalScope (no imports allowed there) — built as a string and
// loaded via a Blob URL so no separate build step/webpack asset is needed for it.
const PCM_WORKLET_SOURCE = `
  class PcmCaptureProcessor extends AudioWorkletProcessor {
    constructor() {
      super();
      this.chunks = [];
      this.bufferedSamples = 0;
    }

    process(inputs) {
      const input = inputs[0];
      const channelData = input && input[0];
      if (channelData && channelData.length) {
        this.chunks.push(channelData.slice());
        this.bufferedSamples += channelData.length;

        if (this.bufferedSamples >= ${PCM_CHUNK_SAMPLES}) {
          const merged = new Float32Array(this.bufferedSamples);
          let offset = 0;
          this.chunks.forEach((chunk) => { merged.set(chunk, offset); offset += chunk.length; });

          const pcm16 = new Int16Array(merged.length);
          for (let i = 0; i < merged.length; i += 1) {
            const sample = Math.max(-1, Math.min(1, merged[i]));
            pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
          }

          this.port.postMessage(pcm16.buffer, [pcm16.buffer]);
          this.chunks = [];
          this.bufferedSamples = 0;
        }
      }
      return true;
    }
  }

  registerProcessor('${PCM_PROCESSOR_NAME}', PcmCaptureProcessor);
`;

type UseVoiceRecorderParams = {
  audioStreamRef: MutableRefObject<MediaStream | null>;
  setInactivityCount: Dispatch<SetStateAction<number>>;
};

type UseVoiceRecorderReturn = {
  // Streams the learner's turn as LINEAR16 PCM chunks via onPcmChunk, resolving once local
  // silence detection decides the learner is done speaking (or never started speaking).
  recordLearnerTurn: (abortSignal: AbortSignal, onPcmChunk: (bytes: ArrayBuffer) => void) => Promise<void>;
  setMicActive: (active: boolean) => void;
  initializeAnalyser: (stream: MediaStream) => void;
  cleanupRecorder: () => void;
  // Ends the in-flight recordLearnerTurn() call immediately — call this when the AI
  // service reports the final transcript is ready (transcript_final), so the turn ends
  // on whichever signal (that, or our local amplitude-based POST_SPEECH_SILENCE_MS
  // timeout) fires first.
  notifyTranscriptFinal: () => void;
};

const useVoiceRecorder = ({
  audioStreamRef,
  setInactivityCount,
}: UseVoiceRecorderParams): UseVoiceRecorderReturn => {
  const silenceCheckFrameRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // Holds the current turn's "end it now" function while recordLearnerTurn is in flight,
  // so notifyTranscriptFinal has something to call — null the rest of the time.
  const earlyStopRef = useRef<(() => void) | null>(null);

  const stopSilenceDetection = useCallback(() => {
    if (silenceCheckFrameRef.current !== null) {
      cancelAnimationFrame(silenceCheckFrameRef.current);
      silenceCheckFrameRef.current = null;
    }
  }, []);

  const setMicActive = useCallback((active: boolean) => {
    audioStreamRef.current?.getAudioTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign
      track.enabled = active;
    });
  }, [audioStreamRef]);

  const initializeAnalyser = useCallback((stream: MediaStream) => {
    analyserRef.current = getAudioAnalyzer(stream);
  }, []);

  const cleanupRecorder = useCallback(() => {
    stopSilenceDetection();
    analyserRef.current = null;
  }, [stopSilenceDetection]);

  // Resolves when speech ends (post-speech silence), or when no speech is detected at all
  // within PRE_SPEECH_SILENCE_MS (triggers "are you still there?" flow) — in both cases the
  // audio itself has already reached the server via onPcmChunk, so nothing is returned.
  const recordLearnerTurn = useCallback((
    abortSignal: AbortSignal,
    onPcmChunk: (bytes: ArrayBuffer) => void,
  ): Promise<void> => {
    const stream = audioStreamRef.current;
    if (!stream) {
      return Promise.reject(new Error('No audio stream'));
    }

    return new Promise<void>((resolve, reject) => {
      let captureContext: AudioContext | null = null;
      let sourceNode: MediaStreamAudioSourceNode | null = null;
      let workletNode: AudioWorkletNode | null = null;

      const stopCapture = () => {
        if (workletNode) {
          workletNode.port.onmessage = null;
          workletNode.disconnect();
        }
        sourceNode?.disconnect();
        captureContext?.close().catch(() => {});
      };

      let hasSpeechDetected = false;
      let lastSpeechTime = Date.now();
      let preSpeechSilenceTimer: ReturnType<typeof setTimeout> | null = null;

      const cleanupTimers = () => {
        stopSilenceDetection();
        if (preSpeechSilenceTimer !== null) { clearTimeout(preSpeechSilenceTimer); preSpeechSilenceTimer = null; }
      };

      const onAbort = () => {
        earlyStopRef.current = null;
        cleanupTimers();
        stopCapture();
        reject(new DOMException('Recording aborted', 'AbortError'));
      };

      if (abortSignal.aborted) {
        onAbort();
        return;
      }
      abortSignal.addEventListener('abort', onAbort, { once: true });

      const finishRecording = () => {
        earlyStopRef.current = null;
        abortSignal.removeEventListener('abort', onAbort);
        cleanupTimers();
        stopCapture();
        resolve();
      };

      // The server's own transcript_final ends the turn the same way our local silence
      // detection does — whichever of the two fires first wins.
      earlyStopRef.current = finishRecording;

      const finishWithSilence = () => {
        earlyStopRef.current = null;
        abortSignal.removeEventListener('abort', onAbort);
        cleanupTimers();
        stopCapture();
        resolve();
        setInactivityCount((prevCount) => prevCount + 1);
      };

      preSpeechSilenceTimer = setTimeout(finishWithSilence, PRE_SPEECH_SILENCE_MS);

      const checkSilence = () => {
        if (abortSignal.aborted) return;

        if (analyserRef.current) {
          const volume = getVolume(analyserRef.current);

          if (volume > SILENCE_THRESHOLD) {
            if (!hasSpeechDetected) {
              hasSpeechDetected = true;
              if (preSpeechSilenceTimer !== null) {
                clearTimeout(preSpeechSilenceTimer);
                preSpeechSilenceTimer = null;
              }
            }
            lastSpeechTime = Date.now();
          } else if (hasSpeechDetected && (Date.now() - lastSpeechTime) >= POST_SPEECH_SILENCE_MS) {
            finishRecording();
            return;
          }
        }

        silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
      };

      const startCapture = async () => {
        try {
          captureContext = new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
          const moduleBlob = new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' });
          const moduleUrl = URL.createObjectURL(moduleBlob);
          try {
            await captureContext.audioWorklet.addModule(moduleUrl);
          } finally {
            URL.revokeObjectURL(moduleUrl);
          }

          if (abortSignal.aborted) {
            captureContext.close().catch(() => {});
            return;
          }

          sourceNode = captureContext.createMediaStreamSource(stream);
          workletNode = new AudioWorkletNode(captureContext, PCM_PROCESSOR_NAME);
          workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => onPcmChunk(event.data);
          sourceNode.connect(workletNode);
          // A node not connected through to destination isn't guaranteed to be pulled/
          // processed — route through a silent (gain=0) sink so the worklet keeps running
          // without the learner's own mic audio being played back to them.
          const silentSink = captureContext.createGain();
          silentSink.gain.value = 0;
          workletNode.connect(silentSink);
          silentSink.connect(captureContext.destination);

          silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
        } catch (err) {
          earlyStopRef.current = null;
          abortSignal.removeEventListener('abort', onAbort);
          cleanupTimers();
          reject(err);
        }
      };

      startCapture();
    });
  }, [audioStreamRef, stopSilenceDetection, setInactivityCount]);

  const notifyTranscriptFinal = useCallback(() => {
    earlyStopRef.current?.();
  }, []);

  return {
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
    notifyTranscriptFinal,
  };
};

export default useVoiceRecorder;
